import { useEffect, useState } from "react";
import { Camera, CheckCircle2, Loader2, ShieldAlert, ShieldCheck } from "lucide-react";
import AuthImage from "@/components/AuthImage";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

function Verdict({ verdict }) {
  if (!verdict || typeof verdict !== "object") return null;
  const ok = verdict.compliant !== false;
  const Icon = ok ? ShieldCheck : ShieldAlert;
  return (
    <div className={`mt-1 flex items-start gap-1 text-[11px] font-body ${ok ? "text-emerald-800" : "text-rose-700"}`}>
      <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" />
      <span className="line-clamp-2">
        IA: {ok ? "conforme" : "não conforme"}
        {verdict.confidence != null ? ` (${Math.round(Number(verdict.confidence) * (Number(verdict.confidence) <= 1 ? 100 : 1))}%)` : ""}
        {verdict.summary ? ` · ${verdict.summary}` : ""}
      </span>
    </div>
  );
}

export default function OccurrenceEvidence({ occurrence }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!occurrence?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    api
      .get(`/occurrences/${occurrence.id}/evidence`)
      .then(({ data: d }) => !cancelled && setData(d))
      .catch(() => !cancelled && setData({ photos: occurrence.photos?.map((p) => ({ photo_url: p, label: "Foto anexada" })) || [] }))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [occurrence?.id, occurrence?.resolution_photos?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-stone-500 font-body" data-testid="occurrence-evidence-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando evidências…
      </div>
    );
  }
  const photos = data?.photos || [];
  const after = occurrence.resolution_photos?.length ? occurrence.resolution_photos : data?.resolution_photos || [];
  if (photos.length === 0 && after.length === 0) return null;

  return (
    <div data-testid="occurrence-evidence" className="space-y-4">
      {photos.length > 0 && (
        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-rose-700 font-body flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> {after.length > 0 ? "Antes" : "Evidências"} ({photos.length})
            </div>
            {data?.execution?.template_name && (
              <div className="text-[11px] text-stone-500 font-body truncate max-w-[60%]">
                {data.execution.template_name}
                {data.execution.completed_at ? ` · ${formatDateTime(data.execution.completed_at)}` : ""}
              </div>
            )}
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p, i) => (
              <figure key={p.photo_url} className="rounded-lg border border-stone-200 bg-white overflow-hidden" data-testid={`occurrence-photo-${i}`}>
                <AuthImage src={p.photo_url} alt={p.label} className="h-28 w-full object-cover cursor-zoom-in" />
                <figcaption className="p-2">
                  <div className="text-xs font-semibold text-stone-800 font-body line-clamp-2">{p.label}</div>
                  {p.value && p.value !== p.photo_url && (
                    <div className="text-[11px] text-stone-500 font-body truncate">Resposta: {p.value}</div>
                  )}
                  <Verdict verdict={p.ai_verdict} />
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
      {after.length > 0 && (
        <div data-testid="occurrence-after-gallery">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-800 font-body flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" /> Depois ({after.length})
            {occurrence.resolved_at && <span className="text-[11px] font-normal normal-case tracking-normal text-stone-500">· resolvida em {formatDateTime(occurrence.resolved_at)}</span>}
          </div>
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {after.map((u, i) => (
              <figure key={u} className="rounded-lg border border-emerald-200 bg-emerald-50/40 overflow-hidden" data-testid={`occurrence-after-${i}`}>
                <AuthImage src={u} alt={`Depois ${i + 1}`} className="h-28 w-full object-cover cursor-zoom-in" />
                <figcaption className="p-2 text-xs font-semibold text-emerald-900 font-body">Comprovação {i + 1}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      )}
      <p className="text-[11px] text-stone-400 font-body -mt-2">Clique na foto para ampliar.</p>
    </div>
  );
}
